import React, { useCallback, useMemo } from 'react';
import { Box, Button, Typography, Alert } from '@mui/material';
import { Add } from '@mui/icons-material';
import { SortRule } from './SortRule';
import type { LogisticsObjectScope } from '@/types/logisticsObject';
import { SortOrder } from '@/api/bol';

export interface SortBuilderProps {
  value: Array<{ [key: string]: SortOrder }>;
  onChange: (value: Array<{ [key: string]: SortOrder }>) => void;
  disabled?: boolean;
  logisticsObjectType?: LogisticsObjectScope;
}

const MAX_SORT_RULES = 10; // Per OpenAPI spec

export const SortBuilder: React.FC<SortBuilderProps> = ({
  value,
  onChange,
  disabled = false,
  logisticsObjectType = 'all'
}) => {
  const sortRules = useMemo(() => value || [], [value]);

  const handleAddRule = useCallback(() => {
    const newRule = { '': 'asc' as SortOrder };
    onChange([...sortRules, newRule]);
  }, [sortRules, onChange]);

  const handleRemoveRule = useCallback((index: number) => {
    const newRules = sortRules.filter((_, i) => i !== index);
    onChange(newRules);
  }, [sortRules, onChange]);

  const handleUpdateRule = useCallback((index: number, field: string, order: SortOrder) => {
    const newRules = [...sortRules];
    newRules[index] = { [field]: order };
    onChange(newRules);
  }, [sortRules, onChange]);

  const handleMoveUp = useCallback((index: number) => {
    if (index === 0) return;
    const newRules = [...sortRules];
    [newRules[index - 1], newRules[index]] = [newRules[index], newRules[index - 1]];
    onChange(newRules);
  }, [sortRules, onChange]);

  const handleMoveDown = useCallback((index: number) => {
    if (index === sortRules.length - 1) return;
    const newRules = [...sortRules];
    [newRules[index], newRules[index + 1]] = [newRules[index + 1], newRules[index]];
    onChange(newRules);
  }, [sortRules, onChange]);

  const atMaxLimit = sortRules.length >= MAX_SORT_RULES;

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Sort Rules ({sortRules.length}/{MAX_SORT_RULES})
        </Typography>
        <Button
          startIcon={<Add />}
          onClick={handleAddRule}
          disabled={disabled || atMaxLimit}
          size="small"
          variant="outlined"
        >
          Add Sort Rule
        </Button>
      </Box>

      {/* Max limit warning */}
      {atMaxLimit && (
        <Alert severity="warning" sx={{ mb: 0 }}>
          Maximum of {MAX_SORT_RULES} sort rules reached
        </Alert>
      )}

      {/* Sort Rules List */}
      {sortRules.length === 0 ? (
        <Box 
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            border: 1, 
            borderColor: 'divider', 
            borderRadius: 1,
            borderStyle: 'dashed',
            backgroundColor: 'background.default'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No sort rules defined. Click &quot;Add Sort Rule&quot; to begin.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sortRules.map((rule, index) => {
            const field = Object.keys(rule)[0] || '';
            const order = rule[field] || 'asc';

            return (
              <SortRule
                key={index}
                field={field}
                order={order}
                onChange={(newField, newOrder) => handleUpdateRule(index, newField, newOrder)}
                onRemove={() => handleRemoveRule(index)}
                onMoveUp={() => handleMoveUp(index)}
                onMoveDown={() => handleMoveDown(index)}
                disabled={disabled}
                showMoveButtons={sortRules.length > 1}
                canMoveUp={index > 0}
                canMoveDown={index < sortRules.length - 1}
                logisticsObjectType={logisticsObjectType}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};
